import moment from 'moment';
import { hexToRgbTriplet } from '../../../../../config/themePalette';
import { resolveTrendComparePointYear } from '../../../common/lib/statisticsChartPalette';
import {
	detectLineCompareGranularity,
	formatLineCompareAxisLabel,
	TREND_GRANULARITY,
} from '../../../filters/lib/statisticsComparePresets';
import { resolveOverviewPeriodUnit } from '../../../filters/lib/overviewPeriodUnit';
import { formatPrimaryPhaseMetricAxisValue } from '../../pipeline/lib/orderPrimaryPhaseMetric';
import { isTrendBarChart } from './trendChartConfig';
import { isMixedMetricSeriesTypes } from '../../shared/lib/metricSeriesTypes';

const rgbaFromHex = (hex, alpha) => {
	const rgb = hexToRgbTriplet(hex);
	if (!rgb) return `rgba(44, 123, 229, ${alpha})`;
	return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
};

const normalizeCompareNumericValue = (value) => {
	if (value == null) return null;
	if (typeof value === 'object' && 'value' in value) {
		if (value.value == null) return null;
		return Number(value.value) || 0;
	}
	return Number(value) || 0;
};

const collectCompareNumericValues = (...buckets) => {
	const nums = [];
	for (const bucket of buckets) {
		if (bucket == null) continue;
		const list = Array.isArray(bucket) ? bucket : [bucket];
		for (const item of list) {
			if (Array.isArray(item)) {
				for (const nested of item) {
					const num = normalizeCompareNumericValue(nested);
					if (num != null) nums.push(num);
				}
			} else if (typeof item === 'object' && item != null && Array.isArray(item.values)) {
				for (const nested of item.values) {
					const num = normalizeCompareNumericValue(nested);
					if (num != null) nums.push(num);
				}
			} else {
				const num = normalizeCompareNumericValue(item);
				if (num != null) nums.push(num);
			}
		}
	}
	return nums;
};

export const buildCompareAxisPointer = (chartTheme, chartType, metricSeriesTypes = null) => {
	if (metricSeriesTypes && isMixedMetricSeriesTypes(metricSeriesTypes)) {
		return { type: 'cross', crossStyle: { color: chartTheme.axisPointerShadow } };
	}
	return isTrendBarChart(chartType)
		? { type: 'shadow', shadowStyle: { color: chartTheme.axisPointerShadow } }
		: { type: 'line', lineStyle: { color: chartTheme.axisPointerShadow } };
};

export const buildCompareValueYAxis = (chartTheme, { currencyMetric = false, formatAxisValue, values = [], allowFraction = false } = {}) => {
	const nums = collectCompareNumericValues(values);
	const maxVal = nums.length ? Math.max(...nums) : 0;
	const minVal = nums.length ? Math.min(...nums) : 0;
	const flatZero = nums.length > 0 && maxVal === minVal && maxVal === 0;
	const skipMinInterval = currencyMetric || allowFraction || flatZero || Boolean(formatAxisValue);

	return {
		type: 'value',
		...(flatZero ? { min: 0, max: 1, interval: 1 } : { min: currencyMetric || allowFraction ? undefined : 0 }),
		...(skipMinInterval ? {} : { minInterval: 1 }),
		...chartTheme.axis,
		axisLabel: {
			...chartTheme.axis.axisLabel,
			formatter: formatAxisValue ?? (currencyMetric ? formatPrimaryPhaseMetricAxisValue : undefined),
		},
	};
};

/** 时段对比横轴：年→YYYY年；月→YYYY年M月；季→YYYY年Qn（含当年/当月/当季未满整段） */
export const resolvePeriodCompareCategoryLabel = (point = {}) => {
	const startDate = point.startDate || point.periodStartDate || null;
	const endDate = point.endDate || point.periodEndDate || null;
	if (startDate && endDate) {
		const granularity = detectLineCompareGranularity(startDate, endDate);
		if (
			granularity === TREND_GRANULARITY.YEAR ||
			granularity === TREND_GRANULARITY.MONTH ||
			granularity === TREND_GRANULARITY.QUARTER
		) {
			return formatLineCompareAxisLabel(startDate, endDate, granularity);
		}
		const unit = resolveOverviewPeriodUnit(startDate, endDate);
		const start = moment(startDate);
		if (start.isValid()) {
			if (unit === 'month') return start.format('YYYY年M月');
			if (unit === 'quarter') return `${start.format('YYYY年')}Q${Math.floor(start.month() / 3) + 1}`;
			if (unit === 'year') return `${start.format('YYYY年')}`;
		}
	}
	const axisLabel = String(point.axisLabel || '').trim();
	if (axisLabel) return axisLabel;
	const year = resolveTrendComparePointYear(point);
	if (Number.isFinite(year)) return `${year}年`;
	return String(point.title || point.periodLabel || point.label || '').trim();
};

export const resolvePeriodCompareLegendName = (point = {}) => {
	const label = resolvePeriodCompareCategoryLabel(point);
	if (point.kind === 'anchor' && label) return `${label}（基准）`;
	return label;
};

export const resolvePeriodCompareTooltipHeader = (point) => {
	const label = resolvePeriodCompareCategoryLabel(point) || point.axisLabel || point.label;
	if (point.kind === 'anchor') return `基准 · ${label}`;
	return label;
};

export const buildPeriodCompareYearLegendSeries = (timeline = [], pointColors = []) =>
	timeline.map((point, index) => ({
		name: resolvePeriodCompareLegendName(point),
		type: 'line',
		data: [],
		silent: true,
		tooltip: { show: false },
		legendHoverLink: false,
		itemStyle: { color: pointColors[index] },
		lineStyle: { color: pointColors[index], width: 0 },
		symbol: 'none',
	}));

export const buildLinePointLabel = ({ show, formatValue, color, chartTheme, position = 'top' }) =>
	show
		? {
				show: true,
				position,
				distance: 5,
				fontSize: 8,
				fontWeight: 600,
				...(color === null ? {} : { color: color ?? chartTheme?.labelColor }),
				formatter: (params) => {
					const raw = typeof params.value === 'object' ? params.value?.value : params.value;
					if (raw == null) return '';
					return formatValue(raw);
				},
			}
		: { show: false };

const COMPARE_LEGEND_BASELINE_SUFFIX = '（基准）';

/** 对比折线/柱图图例：窄容器下用 scroll 避免多项图例文字重叠 */
export const buildCompareLineLegend = (chartTheme, { data = null, itemCount = null, suffixByName = null } = {}) => {
	const count = itemCount ?? data?.length ?? 0;
	const useScroll = count >= 2;
	const labelColor = chartTheme.labelColor;
	const accentColor = chartTheme.brandPrimary ?? labelColor;
	const suffixMap = suffixByName && typeof suffixByName === 'object' ? suffixByName : {};

	return {
		...(data?.length ? { data } : {}),
		type: useScroll ? 'scroll' : 'plain',
		orient: 'horizontal',
		top: 6,
		left: 12,
		right: 12,
		padding: [6, 10, 10, 10],
		itemGap: 16,
		itemWidth: 10,
		itemHeight: 10,
		icon: 'circle',
		pageIconSize: 11,
		pageButtonItemGap: 6,
		pageIconColor: labelColor,
		pageTextStyle: { color: labelColor, fontSize: 9 },
		formatter: (name) => {
			const text = String(name ?? '');
			let baseText = text;
			let baselineRich = '';
			if (text.endsWith(COMPARE_LEGEND_BASELINE_SUFFIX)) {
				baseText = text.slice(0, -COMPARE_LEGEND_BASELINE_SUFFIX.length);
				baselineRich = `{baselineGap| }{baseline|基准}`;
			}
			const suffix = suffixMap[text] ?? suffixMap[baseText];
			if (suffix) {
				return `${baseText}{summaryGap| }{summary|${suffix}}${baselineRich}`;
			}
			if (baselineRich) return `${baseText}${baselineRich}`;
			return text;
		},
		textStyle: {
			color: labelColor,
			fontSize: 10,
			lineHeight: 14,
			padding: [0, 0, 0, 4],
			rich: {
				baselineGap: {
					width: 6,
					fontSize: 10,
				},
				baseline: {
					fontSize: 8,
					fontWeight: 600,
					color: accentColor,
					backgroundColor: rgbaFromHex(accentColor, 0.1),
					borderColor: rgbaFromHex(accentColor, 0.28),
					borderWidth: 1,
					borderRadius: 3,
					padding: [1, 4, 1, 4],
					lineHeight: 12,
				},
				summaryGap: {
					width: 4,
					fontSize: 10,
				},
				summary: {
					fontSize: 9,
					fontWeight: 600,
					color: labelColor,
					fontVariant: 'tabular-nums',
				},
			},
		},
		animation: false,
		animationDurationUpdate: 0,
	};
};

export const resolveTrendChartGridTop = ({ showPointLabels = false, legendItemCount = 1 } = {}) => {
	const base = showPointLabels ? 44 : 32;
	if (legendItemCount <= 1) return base;
	return base + 30;
};

/** 卡片预览：去掉双轴预留的左右大边距，坐标轴文字与卡片顶栏内容对齐 */
export const tightenPreviewChartGrid = (option) => {
	if (!option || typeof option !== 'object') return option;
	const patchGrid = (grid) => {
		if (!grid || typeof grid !== 'object') return grid;
		return {
			...grid,
			left: 0,
			right: 0,
			containLabel: true,
		};
	};
	const next = { ...option };
	if (Array.isArray(option.grid)) {
		next.grid = option.grid.map(patchGrid);
	} else if (option.grid) {
		next.grid = patchGrid(option.grid);
	}
	if (option.legend && typeof option.legend === 'object' && !Array.isArray(option.legend)) {
		next.legend = {
			...option.legend,
			left: 0,
			right: 0,
			padding: Array.isArray(option.legend.padding) ? [6, 0, 10, 0] : option.legend.padding,
		};
	}
	return next;
};
